/**
 * The external application URL, with our attribution, or nothing.
 *
 * `apply_url` is a free-text column: a company types it into the offer form and
 * it lands here untouched. The detail sheet used to build the destination by
 * string concatenation — `` `${apply_url}?${params}` `` — which is wrong in two
 * different ways at once:
 *
 * - a URL that already carries a query becomes `…?id=5?utm_source=…`, and the
 *   company's own parameter silently stops being read;
 * - a `javascript:` value goes straight into `window.open`.
 *
 * Parsing instead of concatenating fixes the first; the scheme allowlist fixes
 * the second. Returning `null` rather than a best-effort string is deliberate:
 * a caller that cannot open anything must be able to say so, not open something
 * else.
 */
export const buildExternalApplyUrl = (
  applyUrl: string | null | undefined,
  jobOfferId: string,
): string | null => {
  if (!applyUrl) return null;

  let url: URL;
  try {
    url = new URL(applyUrl);
  } catch {
    // Relative or malformed: there is no external site to send anyone to.
    return null;
  }

  // Allowlist, not a denylist: `javascript:` is the one everybody remembers,
  // but `data:` and `blob:` open the same door.
  if (url.protocol !== "http:" && url.protocol !== "https:") return null;

  // `set`, not append: if the company already tracks its own `ref`, ours is the
  // one that must win — it is the value we read back.
  url.searchParams.set("utm_source", "sevedemo.it");
  url.searchParams.set("ref", jobOfferId);

  return url.toString();
};
